import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Clock, 
  User, 
  Plus, 
  Minus, 
  Edit,
  Calendar,
  DollarSign,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { useAssignmentHistory, type AssignmentHistoryEntry } from '@/hooks/compensation/useAssignmentHistory';

// Interface moved to hook file

interface AssignmentHistoryCardProps {
  employeeId?: string;
  ruleId?: string;
  maxEntries?: number;
}

export function AssignmentHistoryCard({
  employeeId,
  ruleId,
  maxEntries = 10,
}: AssignmentHistoryCardProps) {
  const { currencyCode } = useWorkplaceCurrency();
  const { data: historyEntries = [], isLoading, error } = useAssignmentHistory(employeeId, maxEntries);

  const formatValue = (value: any) => {
    if (typeof value === 'number') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      }).format(value);
    }
    return String(value);
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created':
        return <Plus className="h-4 w-4 text-green-500" />;
      case 'assigned':
        return <Plus className="h-4 w-4 text-blue-500" />;
      case 'updated':
        return <Edit className="h-4 w-4 text-yellow-500" />;
      case 'deactivated':
      case 'unassigned':
        return <Minus className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'created':
        return 'Created';
      case 'assigned':
        return 'Assigned';
      case 'updated':
        return 'Updated';
      case 'deactivated':
        return 'Deactivated';
      case 'unassigned':
        return 'Unassigned';
      default:
        return action;
    }
  };

  const getRuleTypeIcon = (ruleType: string) => {
    switch (ruleType) {
      case 'hourly_rate':
      case 'salary':
        return <DollarSign className="h-3 w-3" />;
      case 'overtime':
        return <Clock className="h-3 w-3" />;
      default:
        return <DollarSign className="h-3 w-3" />;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Assignment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Assignment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-destructive py-8">
            <p className="text-sm">Failed to load assignment history</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Assignment History
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-80">
          <div className="space-y-4">
            {historyEntries.length > 0 ? (
              historyEntries.map((entry, index) => (
                <div key={entry.id} className="relative">
                  {/* Timeline line */}
                  {index < historyEntries.length - 1 && (
                    <div className="absolute left-6 top-8 bottom-0 w-px bg-border"></div>
                  )}
                  
                  <div className="flex gap-3">
                    {/* Action icon */}
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                      {getActionIcon(entry.action)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">
                          {getActionLabel(entry.action)}
                        </Badge>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          {getRuleTypeIcon(entry.rule_type)}
                          <span>{entry.rule_type.replace('_', ' ').toUpperCase()}</span>
                        </div>
                      </div>
                      
                      <p className="font-medium text-sm">{entry.rule_name}</p>
                      
                      {entry.employee_name && (
                        <div className="flex items-center gap-2 mt-1">
                          <User className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {entry.employee_name}
                          </span>
                        </div>
                      )}
                      
                      {(entry.old_values || entry.new_values || entry.reason) && (
                        <div className="mt-2 p-2 bg-muted rounded text-xs">
                          {Object.keys(entry.old_values).length > 0 && Object.keys(entry.new_values).length > 0 && (
                            <div className="space-y-1">
                              {Object.keys(entry.new_values).map(key => (
                                entry.old_values[key] !== entry.new_values[key] && (
                                  <div key={key}>
                                    <span className="text-muted-foreground">{key.replace('_', ' ')}: </span>
                                    {entry.old_values[key] && (
                                      <>
                                        <span className="line-through">{formatValue(entry.old_values[key])}</span>
                                        <span className="text-muted-foreground"> → </span>
                                      </>
                                    )}
                                    <span className="font-medium">{formatValue(entry.new_values[key])}</span>
                                  </div>
                                )
                              ))}
                            </div>
                          )}
                          {Object.keys(entry.old_values).length === 0 && Object.keys(entry.new_values).length > 0 && (
                            <div className="space-y-1">
                              {Object.entries(entry.new_values).map(([key, value]) => (
                                <div key={key}>
                                  <span className="text-muted-foreground">{key.replace('_', ' ')}: </span>
                                  <span className="font-medium">{formatValue(value)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {entry.reason && (
                            <div className="mt-1 text-muted-foreground">
                              <strong>Reason:</strong> {entry.reason}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>{format(new Date(entry.performed_at), 'MMM dd, yyyy HH:mm')}</span>
                        <span>•</span>
                        <span>by {entry.performer_name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">No assignment history found</p>
                <p className="text-xs">Changes to compensation rules will appear here</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}